import React, { useState } from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import LogoAdvantagePlus from '../../assets/svg/logo-advantage-plus.svg'
import LogoLoop from '../../assets/svg/logo-loop.svg'
import LogoPersonalArchitectWhite from '../../assets/svg/logo-personal-architect-white.svg'
import LogoNeighborlyWhite from '../../assets/svg/logo-neighborly-white.svg'
import LogoEditionAI from '../../assets/svg/logo-edition-ai.svg'
import NeighborlyTechHubBackgroundImage from '../../assets/webp/step-0.webp' // Advantage Plus
import AdvantagePlusBackgroundImage from '../../assets/webp/step-1.webp' // Advantage Plus
import LoopBackgroundImage from '../../assets/webp/step-2.webp' // Loop
import PersonalArchitectBackgroundImage from '../../assets/webp/step-3.webp' // Personal Architect
import EditionAIBackgroundImage from '../../assets/webp/step-4.webp' // Edition AI

const NeighborlyTechHub = (props) => {

  const neighborlyTechnologyHub = {
    id: "neighborlyTechnologyHub",
    label: "Neighborly Technology Hub",
    color: "#002553",
    logo: LogoNeighborlyWhite,
    backgroundImage: NeighborlyTechHubBackgroundImage,
    headline: "Integrated hub for Neighborly owners and customers to improve tech experiences.",
    description: "Integrated solution that combines a dynamic sales front-end, a centralized design application, and BI to enhance collaboration, boost productivity, and deliver insights.",
  };

  const circleItems = [
    {
      id: "cms",
      label: "CMS",
      color: "#86c6cf",
      logo: LogoEditionAI,
      backgroundImage: EditionAIBackgroundImage,
      headline: "SEO compliant CMS for location based unique content management.",
      description: "Edition AI is a unique content management system with SEO compliance for every location in a network of dealers, resellers or franchisees. Perfect for Neighborly owners.",
    },
    {
      id: "visualizer",
      label: "Visualizer",
      color: "#b71c1c",
      logo: LogoPersonalArchitectWhite,
      backgroundImage: PersonalArchitectBackgroundImage,
      headline: "Custom visualizer that enables customers to design with your products.",
      description: "Personal Architect is a 3D visualizer and design tool that puts your products in front of customers in an engaging and meaningfully way, improving the sales experience.",
    },
    {
      id: "sales",
      label: "Sales",
      color: "#8e9f4c",
      logo: LogoAdvantagePlus,
      backgroundImage: AdvantagePlusBackgroundImage,
      headline: "Sales professionals present and build quotes, using their own pricebook.",
      description: "Advantage+ is a sales system tailored to your organization that intelligently analyzes revenue streams across your network, ensuring your distribution partners can maximize conversions.",
    },
    {
      id: "bi",
      label: "BI",
      color: "#163c51",
      logo: LogoLoop,
      backgroundImage: LoopBackgroundImage,
      headline: "Insights and location-based intelligence to find pain-points in your processes.",
      description: "Loop is intuitive business performance management software built for multi-site organizations that provides location-level insights to increase profitability and improve the sales process.",
    },
  ];

  const [selectedItem, setSelectedItem] = useState(neighborlyTechnologyHub);

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        {/* Left Section (Circle Selection) */}
        <Box sx={{minWidth: props.windowSize.width > 728 ? 600 : 300,}}>
          <Box
            sx={{
              position: "relative",
              width: props.windowSize.width > 728 ? "20em" : "10em",
              height: props.windowSize.width > 728 ? "20em" : "10em",
              aspectRatio: "1 / 1", // Ensure a perfect circle
              borderRadius: "50%",
              border: "5px solid rgba(209, 49, 56, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              my:10,
              flexShrink: 0, // Prevents shrinking
            }}
          >
            {circleItems.map((item, index) => (
              <Box
                onClick={() => { setSelectedItem(item) }}
                onMouseEnter={() => { setSelectedItem(item) }}
                key={item.id}
                sx={{
                  position: "absolute",
                  width: props.windowSize.width > 728 ? "7.5em" : "6em",
                  height: props.windowSize.width > 728 ? "7.5em" : "6em",
                  backgroundColor: item.color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  cursor: "pointer",
                  transform: `rotate(${index * (360 / circleItems.length)}deg) translate(${props.windowSize.width > 728 ? "10em" : "7em"}) rotate(-${index * (360 / circleItems.length)}deg)`,
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: `rotate(${index * (360 / circleItems.length)}deg) translate(${props.windowSize.width > 728 ? "10em" : "7em"}) scale(1.1) rotate(-${index * (360 / circleItems.length)}deg)`,
                  },
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {item.label}
                </Typography>
              </Box>
            ))}

            {/* Center Object */}
            <Box
              onClick={() => { setSelectedItem(neighborlyTechnologyHub) }}
              onMouseEnter={() => { setSelectedItem(neighborlyTechnologyHub) }}
              sx={{
                position: "absolute",
                width: props.windowSize.width > 728 ? "12em" : "9em",
                height: props.windowSize.width > 728 ? "12em" : "9em",
                backgroundColor: "#002553",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                cursor: "pointer",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                {neighborlyTechnologyHub.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Section (Details) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 300px", // Flexible width with minimum size
            maxWidth: props.windowSize.width > 728 ? 600 : 320,
            alignSelf: "stretch", // Ensures even spacing when wrapping
            my: 2,
          }}
        >
          <Paper sx={{ minWidth: 300, maxWidth: "100%", my: 1 }}>
            <Box>
              <Box
                sx={{
                  alignItems: "center",
                  backgroundColor: selectedItem.color,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                  display: "flex",
                  flex: 1,
                  p: 2,
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={selectedItem.logo}
                  sx={{ maxWidth: props.windowSize.width > 728 ? 400 : 320, maxHeight: 60 }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight={500}
                  lineHeight={1}
                  sx={{ p: 2 }}
                >
                  {selectedItem.headline}
                </Typography>
                <Box
                  component="img"
                  maxHeight={600}
                  minHeight={320}
                  minWidth={props.windowSize.width > 728 ? 400 : 300}
                  maxWidth={"100%"}
                  src={selectedItem.backgroundImage}
                  sx={{
                    backgroundImage: selectedItem.backgroundImage,
                    backgroundPosition: "center center",
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              </Box>
              <Box sx={{ p: 2, }}>
                <Typography>{selectedItem.description}</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default NeighborlyTechHub;
